import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../components/PageConfigProvider/constants';
import type { TabContent } from '../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../components/PageConfigProvider/types';
import { FilterId } from '../filters/types/filters';

const hourlyItems: TabContent['items'] = [
  {
    position: { x: 0, width: 16, y: 0, height: 9 },
    id: 'geography-pay-hourly-compensation',
    title: 'Hourly Compensation',
    plot: {
      defaultMetricOptions: ['contractor_pay_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 16, width: 16, y: 0, height: 9 },
    id: 'geography-pay-hourly-time-to-fill',
    title: 'Time to Fill',
    plot: {
      defaultMetricOptions: ['time_to_fill'],
      chartType: 'line',
    },
  },
  {
    position: { x: 32, width: 16, y: 0, height: 18 },
    id: 'geography-pay-hourly-top-roles',
    title: 'Top Roles',
    plot: {
      defaultMetricOptions: ['contractor_pay_rate'],
      chartType: 'box',
      secondaryDimension: 'role',
    },
  },
  {
    position: { x: 32, width: 16, y: 18, height: 18 },
    id: 'geography-pay-hourly-top-companies',
    title: 'Top Companies',
    info: {
      text: 'The most common companies hiring in this geography. More information on company mapping can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#company-mapping',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['contractor_pay_rate'],
      secondaryDimension: 'company',
      chartType: 'box',
    },
  },
  {
    position: { x: 0, width: 32, y: 9, height: 27 },
    id: 'geography-pay-hourly-distribution',
    title: 'Distribution',
    info: {
      text: 'The distribution of base salary. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      chartType: 'distribution',
      defaultMetricOptions: ['contractor_pay_rate'],
      distributionCutoffLower: 1,
      distributionCutoffUpper: 99,
    },
  },
];

const baseItems: TabContent['items'] = [
  {
    position: { x: 0, width: 16, y: 0, height: 9 },
    id: 'geography-pay-base-compensation',
    title: 'Base Compensation',
    info: {
      text: 'The average predicted base salary over time. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['base_salary'],
      chartType: 'line',
    },
  },
  {
    position: { x: 16, width: 16, y: 0, height: 9 },
    id: 'geography-pay-base-time-to-fill',
    title: 'Time to Fill',
    info: {
      text: 'The average number of days that job postings are active before being removed.',
    },
    plot: {
      defaultMetricOptions: ['time_to_fill'],
      chartType: 'line',
    },
  },
  {
    position: { x: 32, width: 16, y: 0, height: 18 },
    id: 'geography-pay-base-top-roles',
    title: 'Top Roles',
    info: {
      text: 'The most common roles within this company. More information on the occupational taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['base_salary'],
      chartType: 'box',
      secondaryDimension: 'role',
    },
  },
  {
    position: { x: 32, width: 16, y: 18, height: 18 },
    id: 'geography-pay-base-top-companies',
    title: 'Top Companies',
    info: {
      text: 'The most common companies hiring in this geography. More information on company mapping can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#company-mapping',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['base_salary'],
      secondaryDimension: 'company',
      chartType: 'box',
    },
  },
  {
    position: { x: 0, width: 32, y: 9, height: 27 },
    id: 'geography-pay-base-distribution',
    title: 'Distribution',
    info: {
      text: 'The distribution of base salary. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      chartType: 'distribution',
      defaultMetricOptions: ['base_salary'],
      distributionCutoffLower: 1,
      distributionCutoffUpper: 99,
    },
  },
];

const totalItems: TabContent['items'] = [
  {
    position: { x: 0, width: 16, y: 0, height: 9 },
    id: 'geography-pay-total-compensation',
    title: 'Total Compensation',
    info: {
      text: 'The average predicted total compensation over time. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['total_compensation'],
      chartType: 'line',
    },
  },
  {
    position: { x: 16, width: 16, y: 0, height: 9 },
    id: 'geography-pay-total-time-to-fill',
    title: 'Time to Fill',
    info: {
      text: 'The average number of days that job postings are active before being removed.',
    },
    plot: {
      defaultMetricOptions: ['time_to_fill'],
      chartType: 'line',
    },
  },
  {
    position: { x: 32, width: 16, y: 0, height: 18 },
    id: 'geography-pay-total-top-roles',
    title: 'Top Roles',
    info: {
      text: 'The most common roles within this geography. More information on the occupational taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['total_compensation'],
      chartType: 'box',
      secondaryDimension: 'role',
    },
  },
  {
    position: { x: 32, width: 16, y: 18, height: 18 },
    id: 'geography-pay-total-top-companies',
    title: 'Top Companies',
    info: {
      text: 'The most common companies hiring in this geography. More information on company mapping can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#company-mapping',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['total_compensation'],
      secondaryDimension: 'company',
      chartType: 'box',
    },
  },
  {
    position: { x: 0, width: 32, y: 9, height: 27 },
    id: 'geography-pay-total-distribution',
    title: 'Distribution',
    info: {
      text: 'The distribution of total compensation. More information on the compensation model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      chartType: 'distribution',
      defaultMetricOptions: ['total_compensation'],
      distributionCutoffLower: 1,
      distributionCutoffUpper: 99,
    },
  },
];

const sharedConfig: Pick<
  TabContent,
  'secondaryFilters' | 'grid' | 'primaryFilter'
> = {
  secondaryFilters: [
    FilterId.DATE_RANGE,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.COMPANY,
    FilterId.ROLE,
    FilterId.SKILL,
    FilterId.CUSTOM_SKILL,
    FilterId.SENIORITY,
    FilterId.KEYWORDS,
    FilterId.TITLE,
    FilterId.DATA_ADJUSTMENTS,
    FilterId.CONTINGENT_WORKERS,
  ].map((id) => ({ id })),
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
  primaryFilter: {
    id: FilterId.GEOGRAPHY,
    selectionLimit: 2,
    selectionDefault: 1,
    tagColors: true,
  },
};

const geographyPayHourlyTab: TabContent = {
  id: 'geography-pay-hourly',
  items: hourlyItems,
  ...sharedConfig,
};

const geographyPayBaseTab: TabContent = {
  id: 'geography-pay-base',
  items: baseItems,
  ...sharedConfig,
};

const geographyPayTotalTab: TabContent = {
  id: 'geography-pay-total',
  items: totalItems,
  ...sharedConfig,
};

export const geographyPay: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.GEOGRAPHY_PAY,
  revelioPageId: RevelioPageId.GEOGRAPHY_PAY,
  name: 'Pay',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: 'geography-pay-hourly',
      label: 'Hourly',
      tabContent: geographyPayHourlyTab,
      isActive: false,
    },
    {
      id: 'geography-pay-base',
      label: 'Base',
      tabContent: geographyPayBaseTab,
      isActive: false,
    },
    {
      id: 'geography-pay-total',
      label: 'Total',
      tabContent: geographyPayTotalTab,
      isActive: true,
    },
  ],
};
